import React, { useContext, useState } from "react";
import { Button } from "../../ui/button";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Home,
  User,
  DollarSign,
  Mail,
  Settings,
  Store,
  Send,
  Users,
  LayoutDashboard,
  Headphones,
  HelpCircle,
  ShieldCheck,
  Bot,
  LogOut,
  Menu,
  Coins,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationPanel from "@/components/Feedback/NotificationPanel";
import BotStatusMonitor from "@/components/Domain/BotStatusMonitor";
import { SecurityModal } from "@/components/Security";
import { AuthContext } from "@/providers/AuthProvider";
import { cn } from "@/lib/utils";

import wallet from "src/assets/Wallet.png";
// import userP from "src/assets/User.png"; // Unused import
import users from "src/assets/Users.png";
import question from "src/assets/Question.png";
import { ReactComponent as LogoSacGPT } from "src/assets/logo-sac.svg";

import "./TopBar.css";
import { useActiveUser } from "src/hooks/useActiveUser";
import { useNotifications } from "src/providers/NotificationProvider";
import { useIsMobile } from "src/hooks/use-mobile";
import getPermission from "src/utils/getPermission";

const color = "#77E191";

const TopBar = ({
  mainSidebarVisible,
  setMainSidebarVisible,
  // Props below are now optional - falls back to context
  notifications: notificationsProp,
  setNotifications: setNotificationsProp,
  onNotificationClick: onNotificationClickProp,
  onClearAllNotifications: onClearAllNotificationsProp,
  onMarkAsRead: onMarkAsReadProp,
  customTitleComponent,
  isSidebarCollapsed,
}) => {
  const { id, hash } = useParams();
  const { user } = useActiveUser();
  const { signOut } = useContext(AuthContext);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const isPartner = user.userType === "partner" || user.userType === "partnerUser";
  const hasFullAccess = user.userGroup?.fullAccess === true;
  const isMobile = useIsMobile();
  const [securityModalOpen, setSecurityModalOpen] = useState(false);

  // Use context values, fallback to props for backwards compatibility
  const notificationContext = useNotifications();
  const notifications = notificationsProp ?? notificationContext.notifications;
  const setNotifications = setNotificationsProp ?? notificationContext.setNotifications;
  const onNotificationClick = onNotificationClickProp ?? notificationContext.handleNotificationClick;
  const onClearAllNotifications = onClearAllNotificationsProp ?? notificationContext.handleClearAllNotifications;
  const onMarkAsRead = onMarkAsReadProp ?? notificationContext.handleMarkAsRead;



  const title = () => {
    switch (pathname) {
      case "/clientes": // legacy redirect → /atendente
        return {
          name: "Conversas",
          img: <Users className="h-5 w-5 text-slate-600" />
        };

      case "/dashboard":
        return {
          name: "Dashboard",
          img: <LayoutDashboard className="h-5 w-5 text-slate-600" />
        };

      case "/atendente":
        return { 
          name: "Painel de Chamados", 
          img: <Headphones className="h-5 w-5 text-slate-600" />
        };

      case "/vincular":
        return { 
          name: "Vincular Contas", 
          img: <Bot className="h-5 w-5 text-slate-600" />
        };

      case `/configuracoes`:
        return { 
          name: "Configurações", 
          img: <Bot className="h-5 w-5 text-slate-600" />
        };

      case `/configuracoes/automatica`:
        return { 
          name: "Configurações", 
          img: <Bot className="h-5 w-5 text-slate-600" />
        };

      case `/conta`:
        return { 
          name: "Perfil", 
          img: <User className="h-5 w-5 text-slate-600" />
        };

      case `/agendamento`:
      case `/agendamento/`:
        return {
          name: "Configuração de agendamento",
          img: <Bot className="h-5 w-5 text-slate-600" />
        };

      case `/agendamento/lembrete`:
        return {
          name: "Lembrete de agendamento",
          img: <Bot className="h-5 w-5 text-slate-600" />
        };

      case `/follow-up`:
        return {
          name: "Follow up",
          img: <Bot className="h-5 w-5 text-slate-600" />
        };

      case "/flows":
        return {
          name: "Funil",
          img: <Send size={20} color={color} className="imgMenu" />
        };

      case "/flows/editor":
        return {
          name: "Editor de Fluxo",
          img: <Send size={20} color={color} className="imgMenu" />
        };

      case `/chat/${id}`:
        return { name: "Clientes", img: users };

      case "/financeiro":
        return { 
          name: "Financeiro e Planos", 
          img: <DollarSign className="h-5 w-5 text-slate-600" />
        };

      case "/financeiro/step1":
        return { 
          name: "Financeiro e Planos", 
          img: <DollarSign className="h-5 w-5 text-slate-600" />
        };

      case "/financeiro/step2":
        return { 
          name: "Financeiro e Planos", 
          img: <DollarSign className="h-5 w-5 text-slate-600" />
        };

      case "/financeiro/step3":
        return { 
          name: "Financeiro e Planos", 
          img: <DollarSign className="h-5 w-5 text-slate-600" />
        };

      case "/financeiro/cartao-de-credito":
        return { 
          name: "Financeiro e Planos", 
          img: <DollarSign className="h-5 w-5 text-slate-600" />
        };

      case "/planos":
        return "Planos de Assinatura";

      case `/planos/${hash}`:
        return "Planos de Assinatura";

      case "/tokens":
        return "Recarga de Tokens";

      case "/carrinho":
        return "Resumo da Compra";

      case "/admin/dashboard":
        return {
          name: "Dashboard",
          img: <Home size={20} color={color} />,
        };

      case "/admin/usuarios":
        return {
          name: "Administradores",
          img: <Users size={20} color={color} />,
        };

      case "/admin/parceiros":
        return {
          name: "Parceiros",
          img: <Store size={20} color={color} />,
        };

      case "/admin/afiliados":
        return {
          name: "Afiliados",
          img: users,
        };

      case "/admin/financeiro":
        return {
          name: "Financeiro",
          img: <DollarSign size={20} color={color} />,
        };

      case `/admin/parceiros/${id}`:
        return {
          name: "Clientes do Parceiro",
          img: <User size={20} color={color} />,
        };

      case "/vendas/clientes":
        return {
          name: "Clientes",
          img: <User size={20} color={color} />,
        };

      case "/vendas/crm":
        return {
          name: "CRM",
          img: <Mail size={20} color={color} />,
        };

      case "/vendas/dashboard":
        return {
          name: "Dashboard",
          img: <Home size={20} color={color} />,
        };

      case "/vendas/demo":
        return {
          name: "Atendente de Demonstração",
          img: <Settings size={20} color={color} />,
        };

      case "/vendas/suporte":
        return { name: "Suporte", img: question };

      case "/suporte":
        return { 
          name: "Suporte", 
          img: <HelpCircle className="h-5 w-5 text-slate-600" />
        };

      case "/acessos":
        return { 
          name: "Gerenciamento de Acessos", 
          img: <ShieldCheck className="h-5 w-5 text-slate-600" />
        };

      case "/widget-web":
        return { 
          name: "Widget de Chat", 
          img: <Bot className="h-5 w-5 text-slate-600" />
        };

      case "/resposta-voz":
        return { 
          name: "Resposta por Voz", 
          img: <Bot className="h-5 w-5 text-slate-600" />
        };

      case `/vendas/conta`:
        return {
          name: "Configurações de Conta",
          img: <Settings size={20} color={color} />,
        };

      //TODO TEMPORARIO ISSO PQ N SEI ONDE COLOCAR A TELA
      case "/conta/implementacao":
        return {
          name: "Configurações de Conta",
          img: <Settings size={20} color={color} />,
        };

      case "/vendas/financeiro":
        return {
          name: "Financeiro",
          img: <DollarSign size={20} color={color} />,
        };

      case "/vendas/financeiro/cartao-de-credito":
        return { name: "Financeiro", img: wallet };

      case "/vendas/tokens":
        return "Recarga de Tokens";

      default:
        return "";
    }
  };


  const routeTitle = title();
  const partnerTitle = {
    '/assistants': new URLSearchParams(search).get('section') === 'messaging' ? 'Mensageria' : 'Escritório',
    '/estatisticas': 'Estatísticas',
    '/conversas': 'Conversas',
    '/mensageria': 'Mensageria',
    '/atendente': 'Atendimento humano',
    '/conta': 'Conta',
    '/flows': 'Disparos',
  }[pathname] || (typeof routeTitle === 'string' ? routeTitle : routeTitle.name);
  const canReadFinance = hasFullAccess || getPermission([
    'READ_CREDIT_CARD', 'READ_PAYMENT_TRANSACTION', 'READ_PAYMENT_SUBSCRIPTION', 'READ_MONTLY_USAGE_HISTORY',
  ]);

  return (
    <div className={`topbar topbar-v2 ${isPartner ? 'partner-topbar font-inter' : ''} ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {isPartner ? (
        <div className="partner-topbar-heading">
          {setMainSidebarVisible && (
            <Button
              id="partner-menu-toggle"
              className="mobile-menu-button"
              variant="ghost"
              size="icon"
              aria-label="Abrir menu"
              aria-expanded={!!mainSidebarVisible}
              aria-haspopup="dialog"
              onClick={() => setMainSidebarVisible(!mainSidebarVisible)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <Link to="/assistants" className="partner-logo" aria-label="SacGPT — Escritório">
            <LogoSacGPT />
          </Link>
          <div className="sr-only">{customTitleComponent || partnerTitle}</div>
        </div>
      ) : (
        <div className="topbar-logo">
          <a href="/" className="flex items-center">
            <LogoSacGPT className={isMobile ? "h-6" : "h-7"} />
          </a>
        </div>
      )}

      {/* Right Section - Notifications Only */}
      <div className="containerRigthTop">
        {/* Demo Mode Indicator */}
        {user.demo && (
          <div className="demo-indicator">
            <div className="cicle"></div>
            <div>MODO DEMO</div>
          </div>
        )}

        {/* Security Button (Partners only) */}
        {(user.userType === "partner" || user.userType === "partnerUser") && (
          <button
            aria-label="Seus dados seguros"
            onClick={() => setSecurityModalOpen(true)}
            className={cn(
              "partner-security-button flex items-center gap-1.5 px-3 py-1.5 rounded-full",
              "bg-emerald-50 hover:bg-emerald-100 text-emerald-700",
              "border border-emerald-200",
              "text-xs font-medium transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-emerald-300",
              isMobile && "px-2"
            )}
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            {!isMobile && <span>Seus dados seguros</span>}
          </button>
        )}

        {/* Bot Status Monitor (Partners only) */}
        {(user.userType === "partner" || user.userType === "partnerUser") && (
          <BotStatusMonitor compact={isMobile} className="partner-token-monitor" />
        )}

        {/* Security Modal */}
        <SecurityModal 
          open={securityModalOpen} 
          onOpenChange={setSecurityModalOpen} 
        />

        {/* User Avatar Dropdown */}
        {(user.userType === "partner" || user.userType === "partnerUser" || user.userType === "admin") && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button aria-label="Menu da conta" className={`${isPartner ? 'partner-account-button' : ''} flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300 ${isMobile ? 'w-[30px] h-[30px]' : 'w-9 h-9'}`}>
                <div className={`rounded-full ${isPartner ? "bg-brand-strong" : "bg-blue-600"} flex items-center justify-center text-white font-medium ${isMobile ? 'w-[30px] h-[30px] text-xs' : 'w-9 h-9 text-sm'}`}>
                  {user.name ? (
                    // Get initials: first letter of first name + first letter of last name
                    (() => {
                      const names = user.name.trim().split(' ').filter(n => n);
                      if (names.length >= 2) {
                        return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
                      }
                      return names[0]?.charAt(0).toUpperCase() || '';
                    })()
                  ) : (
                    <User className={isMobile ? "w-3.5 h-3.5" : "w-4 h-4"} />
                  )}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-slate-900 truncate">{user.name || "Usuário"}</p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
                {user.userType === "admin" && (
                  <p className="text-xs text-blue-600 font-medium mt-1">Administrador</p>
                )}
              </div>
              <DropdownMenuSeparator />
              {isPartner && (
                <>
                  {(hasFullAccess || getPermission(['READ_PAYMENT_SUBSCRIPTION', 'READ_MONTLY_USAGE_HISTORY'])) && (
                    <DropdownMenuItem onClick={() => navigate('/conta')}><User className="w-4 h-4 mr-2" />Conta</DropdownMenuItem>
                  )}
                  {!user.demo && canReadFinance && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/financeiro')}><DollarSign className="w-4 h-4 mr-2" />Financeiro e planos</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/tokens')}><Coins className="w-4 h-4 mr-2" />Comprar tokens</DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/acessos')}><Users className="w-4 h-4 mr-2" />Acessos da equipe</DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onClick={() => signOut()}
                className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Notifications (Partners only) */}
        {(user.userType === "partner" || user.userType === "partnerUser") && (
          <NotificationPanel
            notifications={notifications}
            setNotifications={setNotifications}
            onNotificationClick={onNotificationClick}
            onClearAll={onClearAllNotifications}
            onMarkAsRead={onMarkAsRead}
          />
        )}
      </div>
    </div>
  );
};

export default TopBar;
